import type { RiskAssessmentTemplate } from '@gcv/shared';
import { useQuery } from '@tanstack/react-query';
import { ErrorCodes } from 'errors';

import { ApiError } from 'api/api-util/api-error';
import { RiskAssessmentTemplatesApi } from 'api/RiskAssessmentTemplatesApi';
import { RISK_ASSESSMENT_TEMPLATES_KEY } from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';

/** Keyed by `RiskAssessmentTemplate.id` — the pinned record, not the logical id settings routes on. */
export function useRiskAssessmentTemplate(recordId?: string) {
  const riskAssessmentTemplatesApi = new RiskAssessmentTemplatesApi();
  const { userBankId } = useUser();

  const {
    data: riskAssessmentTemplate,
    isLoading: isLoadingRiskAssessmentTemplate,
    isError: isErrorRiskAssessmentTemplate
  } = useQuery<RiskAssessmentTemplate | null>({
    enabled: !!userBankId.length && !!recordId,
    queryKey: [RISK_ASSESSMENT_TEMPLATES_KEY, userBankId, 'record', recordId],
    queryFn: async () => {
      try {
        return await riskAssessmentTemplatesApi.getRiskAssessmentTemplate(userBankId, recordId as string);
      } catch (error) {
        if (error instanceof ApiError && error.code === ErrorCodes.NotFound) {
          return null;
        }
        throw error;
      }
    }
  });

  return {
    riskAssessmentTemplate: riskAssessmentTemplate ?? undefined,
    isLoadingRiskAssessmentTemplate,
    isErrorRiskAssessmentTemplate
  };
}
